import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Loader2, LogIn, LogOut, Check, ArrowLeft, Delete, AlertTriangle,
} from "lucide-react";
import {
  kioskStoreInfo, kioskState, kioskClockIn, kioskClockOut, kioskStartBreak, kioskEndBreak,
} from "../lib/teamsSchedule";
import { EmployeeGrid } from "../components/kiosk/EmployeeGrid";
import { MyHoursPanel } from "../components/kiosk/MyHoursPanel";
import { BreakControls } from "../components/kiosk/BreakControls";
import { fmtClock } from "../components/kiosk/kioskUtils";
import { ClosingButton } from "../components/kiosk/ClosingButton";
import { ClosingWizard } from "../components/kiosk/ClosingWizard";

/* Simple styling: deliberately touch-friendly, no login shell.
   Public route — no auth. Backend gates on 4-digit passcode. */

const CADENCE_PILL_COLOR = {
  daily: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  weekly: "bg-purple-500/20 text-purple-300 border-purple-500/40",
  biweekly: "bg-pink-500/20 text-pink-300 border-pink-500/40",
  monthly: "bg-orange-500/20 text-orange-300 border-orange-500/40",
  adhoc: "bg-amber-500/20 text-amber-300 border-amber-500/40",
};

const Kiosk = () => {
  const { storeId } = useParams();
  const [store, setStore] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);  // {id, name, store_id}
  const [passcode, setPasscode] = useState("");
  const [pinError, setPinError] = useState("");
  const [authed, setAuthed] = useState(null);           // { employee, state, tasks_today, open_entry }
  const [taskState, setTaskState] = useState({});       // template_id → 'done'|'skipped'
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hoursKey, setHoursKey] = useState(0);
  const [closing, setClosing] = useState(null);
  const [closingMode, setClosingMode] = useState(false);

  // Clock ticker
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const loadStore = useCallback(async (initial = false) => {
    try {
      const info = await kioskStoreInfo(storeId);
      setStore(info.store);
      setEmployees(info.employees || []);
      setClosing(info.closing || null);
    } catch (e) {
      if (initial) setError(e?.response?.data?.detail || "Unable to load this kiosk store. Check the URL.");
    } finally { if (initial) setLoading(false); }
  }, [storeId]);

  useEffect(() => {
    loadStore(true);
    const t = setInterval(() => loadStore(false), 60_000);
    return () => clearInterval(t);
  }, [loadStore]);

  const resetKeypad = useCallback(() => {
    setPasscode(""); setPinError("");
  }, []);

  const goHome = useCallback(() => {
    setSelectedEmp(null); setAuthed(null); setTaskState({}); resetKeypad(); setClosingMode(false);
    loadStore(false);
  }, [resetKeypad, loadStore]);

  const pickEmp = (emp) => {
    setSelectedEmp(emp); resetKeypad();
  };

  const submitPasscode = useCallback(async (pc) => {
    if (!selectedEmp) return;
    if (!/^\d{4}$/.test(pc)) return;
    setBusy(true); setPinError("");
    try {
      const res = await kioskState({ employee_id: selectedEmp.id, passcode: pc });
      setAuthed(res);
      // Prepopulate task_state from open_entry.task_results if already clocked in
      const seed = {};
      (res?.open_entry?.task_results || []).forEach((r) => { seed[r.template_id] = r.status; });
      setTaskState(seed);
    } catch (e) {
      setPinError(e?.response?.data?.detail || "Invalid passcode");
      setPasscode("");
    } finally { setBusy(false); }
  }, [selectedEmp]);

  // Auto-submit when 4 digits entered
  useEffect(() => {
    if (passcode.length === 4 && selectedEmp && !authed) {
      submitPasscode(passcode);
    }
  }, [passcode, selectedEmp, authed, submitPasscode]);

  const pressKey = (k) => {
    setPinError("");
    if (k === "back") return setPasscode((p) => p.slice(0, -1));
    if (k === "clear") return setPasscode("");
    setPasscode((p) => (p.length >= 4 ? p : p + k));
  };

  const doClockIn = async () => {
    if (!selectedEmp) return;
    setBusy(true);
    try {
      await kioskClockIn({ employee_id: selectedEmp.id, passcode });
      // Refresh state
      const s = await kioskState({ employee_id: selectedEmp.id, passcode });
      setAuthed(s);
      setTaskState({});
      setHoursKey((k) => k + 1);
    } catch (e) {
      setPinError(e?.response?.data?.detail || "Clock-in failed");
    } finally { setBusy(false); }
  };

  const doClockOut = async () => {
    if (!selectedEmp) return;
    setBusy(true);
    try {
      const tasks = authed?.tasks_today || [];
      const task_results = tasks.map((t) => ({
        template_id: t.id, title: t.title, cadence: t.cadence,
        status: taskState[t.id] === "done" ? "done" : "skipped",
      }));
      await kioskClockOut({ employee_id: selectedEmp.id, passcode, task_results });
      // Show a confirmation, then go home
      setAuthed({ state: "clocked_out", justClockedOut: true, tasks_today: [] });
      setTimeout(goHome, 3000);
    } catch (e) {
      setPinError(e?.response?.data?.detail || "Clock-out failed");
    } finally { setBusy(false); }
  };

  const doBreak = async (start) => {
    if (!selectedEmp) return;
    setBusy(true); setPinError("");
    try {
      const payload = { employee_id: selectedEmp.id, passcode };
      await (start ? kioskStartBreak(payload) : kioskEndBreak(payload));
      setAuthed(await kioskState(payload));
      setHoursKey((k) => k + 1);
    } catch (e) {
      setPinError(e?.response?.data?.detail || "Break update failed");
    } finally { setBusy(false); }
  };

  const toggleTask = (id) => {
    setTaskState((s) => ({ ...s, [id]: s[id] === "done" ? "skipped" : "done" }));
  };

  /* ---------------- Render ---------------- */
  if (loading) {
    return <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center"><Loader2 size={40} className="animate-spin" /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <AlertTriangle size={56} className="mx-auto mb-4 text-yellow-400" />
          <div className="text-lg font-semibold">{error}</div>
          <div className="text-sm mt-2 text-neutral-400">Ask the owner for the correct kiosk URL.</div>
        </div>
      </div>
    );
  }

  const showJustClockedOut = authed?.justClockedOut;
  const inClosing = closingMode && authed && !showJustClockedOut;
  const isOnBreak = !closingMode && authed && !showJustClockedOut && authed.state === "on_break";
  const isClockedIn = !closingMode && authed && !showJustClockedOut && (authed.state === "clocked_in" || isOnBreak);
  const isClockedOut = !closingMode && authed && !showJustClockedOut && authed.state === "clocked_out";

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex flex-col">
      {/* Header */}
      <header className="p-6 flex items-center justify-between border-b border-neutral-800" data-testid="kiosk-header">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-yellow-400">Employee Kiosk</div>
          <div className="text-2xl font-bold mt-1" data-testid="kiosk-store-name">{store?.name}</div>
        </div>
        <div className="text-right">
          <div className="text-4xl font-mono font-bold" data-testid="kiosk-clock">{now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })}</div>
          <div className="text-xs text-neutral-400 mt-1">{now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone: "America/New_York" })}</div>
        </div>
      </header>

      <main className="flex-1 p-6">
        {/* Step 1: Pick employee */}
        {!selectedEmp && !closingMode && <ClosingButton closing={closing} onStart={() => setClosingMode(true)} />}
        {!selectedEmp && closingMode && (
          <button onClick={goHome} className="text-neutral-400 hover:text-white flex items-center gap-2 text-sm mb-4" data-testid="kiosk-closing-exit">
            <ArrowLeft size={14} /> Cancel closing
          </button>
        )}
        {!selectedEmp && (
          <EmployeeGrid employees={employees} onPick={pickEmp}
            title={closingMode ? "Closing — tap your name, then enter your PIN" : undefined} />
        )}
        {inClosing && <ClosingWizard employee={selectedEmp} passcode={passcode} onExit={goHome} />}

        {/* Step 2: Passcode */}
        {selectedEmp && !authed && (
          <div className="max-w-sm mx-auto text-center" data-testid="kiosk-keypad">
            <button onClick={() => { setSelectedEmp(null); resetKeypad(); }} className="text-neutral-400 hover:text-white flex items-center gap-2 text-sm mb-6" data-testid="kiosk-back">
              <ArrowLeft size={14} /> Back
            </button>
            <div className="mb-2 text-neutral-400">Welcome,</div>
            <div className="text-3xl font-bold mb-6" data-testid="kiosk-selected-name">{selectedEmp.name}</div>
            <div className="mb-4 text-sm text-neutral-400">Enter your 4-digit passcode</div>
            <div className="flex justify-center gap-3 mb-6">
              {[0, 1, 2, 3].map((i) => (
                <div key={i}
                  className={`w-14 h-16 border-2 rounded flex items-center justify-center text-3xl font-mono ${
                    pinError ? "border-red-500" : passcode.length > i ? "border-yellow-400 bg-yellow-400/10" : "border-neutral-700"
                  }`}
                  data-testid={`kiosk-pin-slot-${i}`}
                >
                  {passcode[i] ? "•" : ""}
                </div>
              ))}
            </div>
            {pinError && <div className="text-red-500 text-sm mb-4" data-testid="kiosk-pin-error">{pinError}</div>}

            <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                <button key={d} onClick={() => pressKey(String(d))}
                  className="h-16 text-2xl font-bold bg-neutral-900 border border-neutral-800 rounded hover:bg-neutral-800 active:bg-yellow-400 active:text-black"
                  data-testid={`kiosk-key-${d}`}>{d}</button>
              ))}
              <button onClick={() => pressKey("clear")}
                className="h-16 text-sm font-bold bg-neutral-900 border border-neutral-800 rounded hover:bg-neutral-800"
                data-testid="kiosk-key-clear">Clear</button>
              <button onClick={() => pressKey("0")}
                className="h-16 text-2xl font-bold bg-neutral-900 border border-neutral-800 rounded hover:bg-neutral-800 active:bg-yellow-400 active:text-black"
                data-testid="kiosk-key-0">0</button>
              <button onClick={() => pressKey("back")}
                className="h-16 flex items-center justify-center bg-neutral-900 border border-neutral-800 rounded hover:bg-neutral-800"
                data-testid="kiosk-key-back"><Delete size={20} /></button>
            </div>
            {busy && <div className="mt-4"><Loader2 size={20} className="animate-spin inline" /></div>}
          </div>
        )}

        {/* Step 3: Authed — clocked out */}
        {isClockedOut && (
          <div className="max-w-2xl mx-auto" data-testid="kiosk-clockin-view">
            <button onClick={goHome} className="text-neutral-400 hover:text-white flex items-center gap-2 text-sm mb-4" data-testid="kiosk-not-you-back"><ArrowLeft size={14} /> Not you? Go back</button>
            <div className="text-neutral-400 mb-1">Hi,</div>
            <h2 className="text-3xl font-bold mb-6" data-testid="kiosk-hello-name">{selectedEmp.name}</h2>
            <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 mb-6">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-neutral-400 mb-3">Tasks for today ({(authed?.tasks_today || []).length})</div>
              {(authed?.tasks_today || []).length === 0 ? (
                <div className="text-neutral-500 text-sm">No specific tasks today — just handle whatever comes in!</div>
              ) : (
                <ul className="space-y-2">
                  {authed.tasks_today.map((t) => (
                    <li key={t.id} className="flex items-center gap-3 text-sm">
                      <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${CADENCE_PILL_COLOR[t.cadence] || ""}`}>
                        {t.cadence}
                      </span>
                      <span className="font-medium">{t.title}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button onClick={doClockIn} disabled={busy}
              className="w-full py-6 bg-green-500 hover:bg-green-400 text-black text-2xl font-bold rounded-lg flex items-center justify-center gap-3 disabled:opacity-60"
              data-testid="kiosk-clock-in-btn">
              {busy ? <Loader2 size={24} className="animate-spin" /> : <LogIn size={26} />} Clock In
            </button>
            {pinError && <div className="text-red-500 text-sm mt-3 text-center">{pinError}</div>}
            <MyHoursPanel employeeId={selectedEmp.id} passcode={passcode} refreshKey={hoursKey} />
          </div>
        )}

        {/* Step 4: Authed — clocked in */}
        {isClockedIn && (
          <div className="max-w-2xl mx-auto" data-testid="kiosk-clockout-view">
            <div className="text-neutral-400 mb-1">Clocked in since</div>
            <div className="text-2xl font-mono font-bold mb-1" data-testid="kiosk-clockin-time">{fmtClock(authed.open_entry?.clock_in_at)}</div>
            <div className="text-neutral-400 text-sm mb-6">{selectedEmp.name}</div>
            <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 mb-6">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-neutral-400 mb-3">Check off completed tasks</div>
              {(authed?.tasks_today || []).length === 0 ? (
                <div className="text-neutral-500 text-sm">No specific tasks today.</div>
              ) : (
                <ul className="space-y-2">
                  {authed.tasks_today.map((t) => {
                    const done = taskState[t.id] === "done";
                    return (
                      <li key={t.id}>
                        <button onClick={() => toggleTask(t.id)}
                          className={`w-full flex items-center gap-3 px-3 py-3 rounded border ${done ? "bg-green-500/20 border-green-500 text-green-100" : "bg-neutral-950 border-neutral-800 hover:bg-neutral-900"}`}
                          data-testid={`kiosk-task-${t.id}`}>
                          <div className={`w-6 h-6 shrink-0 rounded-full border-2 flex items-center justify-center ${done ? "bg-green-500 border-green-500 text-black" : "border-neutral-600"}`}>
                            {done && <Check size={14} strokeWidth={3} />}
                          </div>
                          <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${CADENCE_PILL_COLOR[t.cadence] || ""}`}>
                            {t.cadence}
                          </span>
                          <span className={`font-medium ${done ? "line-through opacity-70" : ""}`}>{t.title}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <BreakControls onBreak={isOnBreak} breakSince={authed.open_break?.start_at} busy={busy}
              onStart={() => doBreak(true)} onEnd={() => doBreak(false)} />
            <button onClick={doClockOut} disabled={busy || isOnBreak}
              title={isOnBreak ? "End your break before clocking out" : undefined}
              className="w-full py-6 bg-yellow-400 hover:bg-yellow-300 text-black text-2xl font-bold rounded-lg flex items-center justify-center gap-3 disabled:opacity-60"
              data-testid="kiosk-clock-out-btn">
              {busy ? <Loader2 size={24} className="animate-spin" /> : <LogOut size={26} />} Clock Out
            </button>
            {pinError && <div className="text-red-500 text-sm mt-3 text-center">{pinError}</div>}
            <div className="mt-4 text-center">
              <button onClick={goHome} className="text-neutral-500 hover:text-white text-xs" data-testid="kiosk-leave-clocked-in">Done — leave clocked in</button>
            </div>
            <MyHoursPanel employeeId={selectedEmp.id} passcode={passcode} refreshKey={hoursKey} />
          </div>
        )}

        {/* Step 5: Just clocked out — brief confirmation */}
        {showJustClockedOut && (
          <div className="max-w-md mx-auto text-center py-12" data-testid="kiosk-clockout-confirm">
            <div className="w-24 h-24 rounded-full bg-green-500 text-black flex items-center justify-center mx-auto mb-6">
              <Check size={48} strokeWidth={3} />
            </div>
            <div className="text-3xl font-bold mb-2">Have a great one, {selectedEmp.name}!</div>
            <div className="text-neutral-400">Clocked out. See you next shift.</div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Kiosk;
